import React from 'react';
import { createPortal } from 'react-dom';
import { TeButton, TeContainer, TeText } from '@topcon/tuicl-react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from '@topcon/topcon-utils';

import ErrorExplanationBody from '../../components/ErrorExplainer/ErrorExplanationBody';
import { getErrorContent } from '../../utils/errorContent';
import { AppRoutes } from '../../app/routes/utils';

const GENERIC_CODE = 'generic';

// Matches the design-system top overlay layer (zIndex.overlay.top = 1002) so the
// page covers the app shell chrome (sidebar + brandbar), which sit on the
// overlay.bottom layer.
const OVERLAY_TOP_Z_INDEX = 1002;

const overlayStyle: React.CSSProperties = {
  position: 'fixed',
  inset: 0,
  zIndex: OVERLAY_TOP_Z_INDEX,
  overflow: 'auto',
};

/**
 * Standalone landing page for `/error/:code`, matching the Figma "Direct link
 * access" mockup. Rendered as a full-page overlay (portaled to `document.body`)
 * so it covers the app shell and reads as a dedicated page when opened via a
 * direct/shared link. Falls back to the generic entry when the code is missing
 * or unknown.
 * @returns The full-page error explanation overlay.
 */
function ErrorPage(): React.JSX.Element {
  const { code } = useParams<{ code: string }>();
  const { t } = useTranslation('errors');
  const navigate = useNavigate();

  const resolvedCode = code ?? GENERIC_CODE;
  const content = getErrorContent(t, resolvedCode);

  return createPortal(
    <div style={overlayStyle}>
      <TeContainer column fill grow alignItems="stretch" backgroundColor="background.secondary">
        <TeContainer
          width="100%"
          backgroundColor="background.primary"
          padding={['s', 'm']}
          alignItems="center"
        >
          <TeText size={16} weight={700}>
            {t('explainedTitle', { title: content.title })}
          </TeText>
        </TeContainer>

        <TeContainer grow justifyContent="center" alignItems="center" padding="l">
          <TeContainer
            width={576}
            backgroundColor="background.primary"
            borderRadius={4}
            padding="l"
            column
            gap="m"
          >
            <TeText size={20} weight={700}>
              {content.title}
            </TeText>

            <ErrorExplanationBody content={content} descriptionLabel={t('whatDoesThisMean')} />

            <TeButton
              text={t('returnToMyTopcon')}
              variant="secondary"
              grow
              teclick={() => navigate(AppRoutes.Overview)}
            />
          </TeContainer>
        </TeContainer>
      </TeContainer>
    </div>,
    document.body
  );
}

export default ErrorPage;
